import React, { useState, useRef, useEffect, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Zoom, Thumbs } from "swiper/modules";
import Lightbox from "yet-another-react-lightbox";
import LightboxZoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import type { Swiper as SwiperType } from "swiper";
import Skeleton from "react-loading-skeleton";

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/zoom";
import "swiper/css/thumbs";

import type {
  TransformedProduct,
  TransformedVariation,
} from "../../types/productApiTypes";
import type { RawImage } from "../../../../../types";
import {
  getGalleryImages,
  isGalleryEmpty,
  isValidImage,
  PLACEHOLDER_IMAGE_URL,
} from "../../helpers/productImgHelpers";

interface ProductGalleryProps {
  product: TransformedProduct;
  selectedVariation: TransformedVariation | null;
  variationsLoading?: boolean;
}

interface LightboxSlide {
  src: string;
  alt: string;
}

const ProductGallery: React.FC<ProductGalleryProps> = ({
  product,
  selectedVariation = null,
  variationsLoading = false,
}) => {
  const { t } = useTranslation("common");
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);
  const mainSwiperRef = useRef<{ swiper: SwiperType } | null>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);

  const galleryImages: RawImage[] = getGalleryImages(
    product.images || [],
    selectedVariation,
    variationsLoading
  );

  const lightboxSlides: LightboxSlide[] = galleryImages
    .filter(isValidImage)
    .map((image, index) => ({
      src: image.src,
      alt:
        image.alt ||
        product.name ||
        t("productGallery.imageAlt", { index: index + 1 }),
    }));

  const handleZoomClick = (index: number): void => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleThumbnailClick = (index: number): void => {
    mainSwiperRef.current?.swiper?.slideTo(index);
  };

  useEffect(() => {
    const mainSwiperInstance = mainSwiperRef.current?.swiper;
    if (mainSwiperInstance && !mainSwiperInstance.destroyed) {
      if (!variationsLoading) {
        mainSwiperInstance.slideTo(0, 300);
        mainSwiperInstance.update();
      }
    }
    if (thumbsSwiper && !thumbsSwiper.destroyed) {
      if (!variationsLoading) {
        thumbsSwiper.slideTo(0, 300);
        thumbsSwiper.update();
      }
    }
  }, [
    selectedVariation?.id,
    galleryImages.length,
    variationsLoading,
    thumbsSwiper,
  ]);

  if (variationsLoading) {
    return (
      <div className="product-gallery product-gallery-loading">
        <Skeleton height={450} className="gallery-skeleton" />
        <div className="thumbnails-skeleton">
          <Skeleton
            height={80}
            width={80}
            count={4}
            inline={true}
            style={{ marginRight: "10px" }}
          />
        </div>
      </div>
    );
  }

  if (isGalleryEmpty(galleryImages)) {
    return (
      <div className="product-gallery product-gallery-empty">
        <div className="main-image-container">
          <img
            src={PLACEHOLDER_IMAGE_URL}
            alt={`${product.name || "Product"} placeholder`}
            className="placeholder-image"
          />
        </div>
        <div className="thumbnails-container is-empty"></div>
      </div>
    );
  }

  return (
    <div className="product-gallery">
      <div className="main-image-container">
        <Swiper
          ref={mainSwiperRef as RefObject<any>}
          modules={[Navigation, Zoom, Thumbs]}
          spaceBetween={10}
          navigation={galleryImages.length > 1}
          className="main-swiper"
          thumbs={{
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          zoom={{ maxRatio: 2, toggle: true }}
          loop={galleryImages.length > 1}
        >
          {galleryImages.filter(isValidImage).map((image, index) => (
            <SwiperSlide
              key={
                image.id ? `gallery-${image.id}-${index}` : `fallback-${index}`
              }
            >
              <div className="swiper-zoom-container">
                <img
                  src={image.src}
                  alt={
                    image.alt ||
                    product.name ||
                    t("productGallery.imageAlt", { index: index + 1 })
                  }
                  loading={index === 0 ? "eager" : "lazy"}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <button
          className="zoom-button"
          title={t("productGallery.viewFullSize")}
          onClick={() =>
            handleZoomClick(mainSwiperRef.current?.swiper?.realIndex ?? 0)
          }
          aria-label={t("productGallery.viewLarger")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zM10 7H9v2H7v1h2v2h1v-2h2V9h-2z" />
          </svg>
        </button>
      </div>
      {galleryImages.length > 1 && (
        <div className="thumbnails-container">
          <Swiper
            onSwiper={setThumbsSwiper}
            spaceBetween={10}
            slidesPerView={4}
            freeMode={false}
            watchSlidesProgress={true}
            modules={[Thumbs]}
            className="thumbnail-swiper"
            loop={false}
            breakpoints={{
              768: { slidesPerView: 5 },
              1024: { slidesPerView: 6 },
            }}
          >
            {galleryImages.filter(isValidImage).map((image, index) => (
              <SwiperSlide
                key={
                  image.id
                    ? `thumb-${image.id}-${index}`
                    : `fallback-thumb-${index}`
                }
                onClick={() => handleThumbnailClick(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    handleThumbnailClick(index);
                }}
                aria-label={`View image ${index + 1}`}
              >
                <img
                  src={image.thumbnail || image.src}
                  alt={
                    image.alt ||
                    product.name ||
                    t("productGallery.thumbnailAlt", { index: index + 1 })
                  }
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={lightboxSlides}
        plugins={[LightboxZoom, Thumbnails]}
        zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
        thumbnails={{ position: "bottom", width: 80, height: 80 }}
        styles={{
          container: { backgroundColor: "rgba(0, 0, 0, 0.9)" },
          thumbnailsContainer: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
        }}
        render={{
          iconClose: () => (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="white"
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          ),
        }}
      />
    </div>
  );
};

export default ProductGallery;
